/**
 * Clinical Specification schema — the governed clinical-reference container of
 * a Decision Resource (`config.clinicalSpec`).
 *
 * Every block maps 1:1 to a section of the requirements document
 * "SynerGen LMS Implementation Plan2-CWaves.pdf" (referenced below as §n);
 * `specRef` fields carry that mapping into the data itself so rule-level
 * traceability (§18.1) survives copies and exports. The section-by-section
 * status ledger lives in docs/REQUIREMENTS-TRACEABILITY.md.
 */

/** §7 / §11.3 / §16.1 — sentinel for deployment fields a client must resolve. */
export const CLIENT_CONFIGURATION_REQUIRED = "CLIENT_CONFIGURATION_REQUIRED";

/** §6 — governance class of an executable rule. */
export type RuleClass = "A" | "B" | "C";

export const RULE_CLASS_MEANING: Record<RuleClass, string> = {
  A: "Class A — Mandatory safety / regulatory rule. Shall not be silently weakened through ordinary client configuration. (§6)",
  B: "Class B — SynerGen reference clinical rule. A client may adopt or replace it through its approved clinical-governance process. (§6)",
  C: "Class C — Deployment-specific rule. Remains configurable until a client provides and approves it. (§6)",
};

/** §8.1 — one clinical BP category (kept distinct from operational pathways). */
/** One half-open range term (gte ≤ v < lt) against a masters parameter. */
export interface CategoryBoundTerm {
  param: string;
  gte?: number;
  lt?: number;
}

export interface BpCategory {
  id: string;
  label: string;
  /** Human-readable criteria (display + RAG). */
  criteria: string;
  /** Machine-evaluable boundaries — what lets the engine COMPUTE the category
   *  from captured data instead of asking for it. Parameter-keyed so any
   *  condition can classify on any observable (BP, O2 sat, eGFR…). */
  bounds?: {
    /** how the terms combine (AHA BP: normal/elevated AND, stages OR). */
    join?: "and" | "or";
    terms?: CategoryBoundTerm[];
    /** Legacy BP-specific slots — engine still normalizes them; migrated data uses terms. */
    sbpGte?: number;
    sbpLt?: number;
    dbpGte?: number;
    dbpLt?: number;
  };
  ruleClass?: RuleClass;
}

/** Legacy bounds → parameter-keyed terms (mirror of the engine's normalization). */
export const boundsToTerms = (b: NonNullable<BpCategory["bounds"]>): CategoryBoundTerm[] => {
  if (b.terms && b.terms.length > 0) return b.terms;
  const t: CategoryBoundTerm[] = [];
  if (b.sbpGte !== undefined || b.sbpLt !== undefined) t.push({ param: "bp_systolic", gte: b.sbpGte, lt: b.sbpLt });
  if (b.dbpGte !== undefined || b.dbpLt !== undefined) t.push({ param: "bp_diastolic", gte: b.dbpGte, lt: b.dbpLt });
  return t;
};

/**
 * §9.2 — canonical reference roles for a medication-matrix row. A constrained
 * vocabulary rather than free text so the engine can act on the role: the
 * eligibility evaluation echoes it per class, and the §9.1 firstLineClasses
 * list is DERIVED from the rows carrying "First-line" (studio and engine both
 * derive; the stored list is a fallback for resources without roles set).
 * Legacy free-text roles are tolerated but render as ⚠ uncoded until
 * conformed — same posture as uncoded key-evaluation entries.
 */
export const REFERENCE_ROLES = [
  "First-line",
  "Add-on",
  "Alternative",
  "Compelling indication",
  "Specialist / secondary context",
  "Not recommended",
] as const;
export type ReferenceRole = (typeof REFERENCE_ROLES)[number];

export const isFirstLineRole = (role: string | undefined): boolean =>
  (role ?? "").trim().toLowerCase() === "first-line";

/**
 * §9.3 — one structured eligibility rule on a medication-matrix row.
 * Guard-shaped (param/op/value): the same predicate form the workflow engine
 * already evaluates, so the eligibility engine (iLearn Phase A) runs these
 * against captured parameters and cites the failing rule as the exclusion
 * rationale (§14.2 "ineligible options and reasons").
 */
export interface EligibilityRule {
  id: string;
  /** clinical_parameter key (◆ coded); free text allowed but not executable. */
  param: string;
  op: "equals" | "notEquals" | "gt" | "gte" | "lt" | "lte" | "in" | "notEmpty";
  value?: string | number | boolean | Array<string | number>;
  action: "exclude" | "require-review" | "caution";
  ruleClass?: RuleClass;
  note?: string;
  /** §18.1 — the declared guardrail (guardrails.rules[].id) this rule implements.
   *  Declared on the machinery; the §10.2 record derives its display from it. */
  implementsGuardrail?: string;
}

/** §8.2 / §8.3 Step 4 — one LMS operational pathway's reference logic. */
export interface OperationalPathwaySpec {
  id: string;
  name: string;
  referenceConditions: string[];
  referenceActions: string[];
  ruleClass?: RuleClass;
  specRef?: string;
}

/** §9.2 — one row of the reference medication matrix. */
export interface MedicationMatrixRow {
  id: string;
  medicationClass: string;
  /** Reference into the medication_class master (ATC-keyed); name kept for display. */
  classKey?: string;
  /** Canonical value from REFERENCE_ROLES; legacy free text tolerated as ⚠. */
  referenceRole: string;
  keyEvaluation: string[];
  /** Structured, engine-evaluable per-patient rules for this class. */
  rules?: EligibilityRule[];
  ruleClass?: RuleClass;
}

/** §10.2 — one reference guardrail (trigger → behavior). */
export interface GuardrailRule {
  id: string;
  trigger: string;
  referenceBehavior: string;
  ruleClass?: RuleClass;
  /** Optional link to the workflow transition that enforces it (t_* id). */
  /** Legacy enforcement link — superseded by `enforcedBy`; still honored when that is absent. */
  workflowTransitionId?: string;
  /** DEPRECATED record-side assertion — inverted into machinery declarations
   *  (transitions[].implementsGuardrail / rules[].implementsGuardrail); the
   *  §10.2 display is now DERIVED and read-only. Kept for old snapshots only. */
  enforcedBy?: string;
  /**
   * §18.1 deferral — for claims the reference cannot enforce (no capture, no
   *  integration): states why and where fulfillment happens (e.g. "EHR allergy
   *  interface at deployment — §16.1"). A Class A row must be either
   *  machinery-linked or carry a deferral for the publication gate to pass.
   */
  deferral?: string;
}

/** §11.2 — one row of the authority & escalation reference matrix. */
export interface AuthorityMatrixRow {
  id: string;
  trigger: string;
  referenceBehavior: string;
  deploymentConfiguration: string;
  /** Resolved client value; defaults to CLIENT_CONFIGURATION_REQUIRED. */
  deploymentValue?: string;
}

export interface ClinicalSpec {
  /** §18.1 — document this spec traces to. */
  specSource?: { document: string; note?: string };

  /** §13.1 / §13.2 — Decision Resource identity & scope. */
  profile?: {
    primaryReferenceSME?: string;
    deploymentSME?: string;
    primaryUser?: string;
    decisionPoint?: string;
    scope?: string;
    population?: string;
  };

  /** §5 — clinical source and approval hierarchy (per specification area). */
  clinicalSources?: Array<{
    id: string;
    specification: string;
    primarySources: string;
    synergenConfiguration: string;
  }>;

  /** §8.1 — clinical classification, distinct from operational pathways. */
  classification?: {
    system?: string;
    categories?: BpCategory[];
    treatmentGoal?: { text: string; ruleClass?: RuleClass };
    distinctionNote?: string;
    specRef?: string;
  };

  /**
   * §8.3 Step 3 — etiologic/type classification with referral triggers.
   * Condition-agnostic slot: hypertension fills it with primary vs
   * possible-secondary; a heart-failure resource would fill it with
   * ischemic/non-ischemic; stroke with the TOAST classes. The label is data.
   */
  typeClassification?: {
    /** Section label from data (e.g. "Hypertension Type") — never hardcoded UI. */
    label?: string;
    types?: string[];
    suspicionIndicators?: string[];
    confirmationRule?: string;
    /** Workflow capture field (multiselect) recording observed indicators — the sync target. */
    captureFieldId?: string;
    /** Workflow capture field (select) recording the chosen type — the sync target for `types`.
     *  Guards fork on this field's value (e.g. primary → advance, secondary → escalate). */
    typeCaptureFieldId?: string;
    /** §6 governance class of this classification content (indicators, types). */
    ruleClass?: RuleClass;
    specRef?: string;
  };

  /** §8.2 — the eleven dimensions each pathway maps to. */
  pathwayMappingDimensions?: string[];

  /** §8.2 + §8.3 Step 4 — the four LMS operational pathways. */
  operationalPathways?: OperationalPathwaySpec[];

  /**
   * §8.3 — the requirement-side decision sequence (order, names, §-refs).
   * The state mapping is DERIVED at render time from workflow states'
   * `decisionStep` tags; `workflowStateIds` is only a fallback for
   * resources without an executable workflow.
   */
  decisionSequence?: Array<{
    step: number;
    name: string;
    /** @deprecated hand-maintained mapping — superseded by state.decisionStep tags. */
    workflowStateIds?: string[];
    specRef?: string;
  }>;

  /** §9 — medication decision matrix. */
  medicationMatrix?: {
    /** §9.1 — DERIVED from rows with the First-line role whenever any exist
     *  (kept in sync on every matrix edit); hand-maintained only on legacy
     *  resources whose roles aren't set. */
    firstLineClasses?: string[];
    notAFormularyNote?: string;
    rows?: MedicationMatrixRow[];
    dosingBoundary?: string;
    /** §9.3 — per-category strategy text, keyed by §8.1 category id (normalized
     *  match, e.g. "stage1"); the eligibility engine echoes the entry matching
     *  the patient's computed category. */
    treatmentStrategy?: { stage1: string; stage2: string };
    /** §9.3 — ordered factors applied before determining eligible options. */
    eligibilityFactors?: Array<{ factor: string; ruleClass?: RuleClass }>;
    specRef?: string;
  };

  /** §10 — safety & guardrail matrix. */
  guardrails?: {
    precedence?: string[];
    aiOverrideRule?: string;
    rules?: GuardrailRule[];
    medicationClassChecks?: Array<{ medicationClass: string; evaluate: string[] }>;
    specRef?: string;
  };

  /** §11 — authority & escalation matrix. */
  authority?: {
    principle?: string;
    matrix?: AuthorityMatrixRow[];
    scopes?: Array<{ name: string; value: string }>;
    demoDefault?: string;
    specRef?: string;
  };

  /** §12 — monitoring & outcome specification. */
  monitoring?: {
    baselineEvidence?: string[];
    medicationMonitoring?: { items: string[]; timingNote?: string };
    followUp?: { rule: string; evaluate: string[] };
    outcomeCapture?: {
      clinical: string[];
      medication: string[];
      decision: string[];
      workflow: string[];
    };
    specRef?: string;
  };

  /** §13.2–§13.4 — rubric, AI workspace, RAG sources, prompt set. */
  aiWorkspace?: {
    rubric?: { name: string; levels: Array<{ level: string; score: number }> };
    workspace?: { name: string; settings: string[] };
    ragSources?: { reference: string[]; clientAdds: string[] };
    promptSet?: string[];
    specRef?: string;
  };

  /** §13.5 — publication gate checklist. */
  publicationGate?: {
    items?: Array<{ id: string; requirement: string; status: "pending" | "met" | "na" }>;
    specRef?: string;
  };

  /** §7 / §16.1 / §17.1 — unresolved deployment configuration.
   *  `custom: true` marks rows added through the studio (deletable anywhere);
   *  seeded reference rows are part of the base contract — resolvable on
   *  copies, deletable only on the base itself. */
  clientConfiguration?: {
    unresolved?: Array<{ key: string; area: string; value: string; specRef?: string; custom?: boolean }>;
    productionRule?: string;
  };

  /**
   * §6 Class C / §7 / §9.3 — client formulary & preferences (deployment layer).
   * Intentionally thin on base templates: the reference matrix carries guideline
   * roles, not a formulary (§9.1); client copies populate preferred agents,
   * tiers and restrictions, which feed the §9.3 eligibility factors
   * (formulary / access / preference — all Class C).
   */
  clientFormulary?: {
    preferredAgents?: Array<{
      /** Medication class (matches a §9.2 matrix row / ATC master by name or classKey). */
      medicationClass: string;
      classKey?: string;
      agent?: string;
      tier?: string;
      restriction?: string;
    }>;
    notes?: string;
    specRef?: string;
  };
}

/** Read the clinical spec off a parsed resource config. */
export const getClinicalSpec = (config: any): ClinicalSpec =>
  (config && typeof config === "object" && config.clinicalSpec) || {};

// ─── Clinical Domain layer (domain master resource, config.domainSpec) ───

/**
 * §5 / §13.3 / §18.1 — one authoritative clinical source, as a master record.
 * The first link of the §18.1 traceability chain (Rule → Clinical Source → …)
 * resolves to one of these; conditions and decision resources reference them by id.
 */
export interface ClinicalSourceRecord {
  id: string;
  name: string;
  publisher?: string;
  year?: string;
  type: "guideline" | "drug-labeling" | "specialty-guidance" | "client-policy";
  /** §13.5 gate item "Required clinical sources are approved" keys off this. */
  approvalStatus: "approved" | "draft" | "retired";
  /** §13.3 — whether the source has been RAGified for the AI workspace.
   *  When resourceIds are linked, this is DERIVED from the linked documents'
   *  isRagified flags; the stored value is only used for document-less sources
   *  (e.g. services like DailyMed). */
  ragStatus?: "pending" | "ragified" | "not-applicable";
  /** The source's actual documents: study resources (uploaded PDFs etc.)
   *  RAGified through the existing pipeline. A source is a governed bundle of
   *  study resources — same shape as a learning module, different consumer. */
  resourceIds?: number[];
}

export interface DomainCondition {
  id: string;
  name: string;
  category?: string;
  status: "active" | "planned";
  description?: string;
  /** References into masters.clinicalSources (the master record). */
  sourceIds?: string[];
  /** Legacy free-text sources — superseded by sourceIds, kept for display. */
  guidelineSources?: string[];
}

export interface DomainSpec {
  name: string;
  description?: string;
  conditions: DomainCondition[];
  masters?: {
    ruleClasses?: Array<{ id: string; name: string; definition: string; specRef?: string }>;
    specificationArtifacts?: Array<{ id: string; name: string; specRef?: string }>;
    roles?: string[];
    clinicalSources?: ClinicalSourceRecord[];
  };
}
